import { Pause, Play } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type MouseEvent } from 'react';

const BARRAS = 34;
const VELOCIDADES = [1, 1.5, 2] as const;

function ondas(semente: string): number[] {
  let h = 0;
  for (const ch of semente) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return Array.from({ length: BARRAS }, (_, i) => {
    h = (h * 1103515245 + 12345) >>> 0;
    const base = 0.35 + (((h >>> 16) % 1000) / 1000) * 0.65;
    const borda = Math.min(1, (i + 1) / 4, (BARRAS - i) / 4);
    return Math.max(0.18, base * borda);
  });
}

const mmss = (s: number) =>
  Number.isFinite(s)
    ? `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`
    : '0:00';

export function PlayerAudio({ src }: { src: string }) {
  const audio = useRef<HTMLAudioElement>(null);
  const [tocando, setTocando] = useState(false);
  const [duracao, setDuracao] = useState(0);
  const [atual, setAtual] = useState(0);
  const [vel, setVel] = useState<(typeof VELOCIDADES)[number]>(1);
  const alturas = useMemo(() => ondas(src), [src]);
  const progresso = duracao ? atual / duracao : 0;

  useEffect(() => {
    const a = audio.current;
    if (!a) return;
    const aoCarregar = () => {
      if (a.duration === Infinity) {
        a.currentTime = 1e101;
        a.addEventListener('timeupdate', function corrigir() {
          a.removeEventListener('timeupdate', corrigir);
          setDuracao(a.duration);
          a.currentTime = 0;
        });
      } else {
        setDuracao(a.duration);
      }
    };
    const aoTempo = () => setAtual(a.currentTime);
    const aoFim = () => {
      setTocando(false);
      setAtual(0);
    };
    a.addEventListener('loadedmetadata', aoCarregar);
    a.addEventListener('timeupdate', aoTempo);
    a.addEventListener('ended', aoFim);
    a.addEventListener('pause', () => setTocando(false));
    a.addEventListener('play', () => setTocando(true));
    return () => {
      a.removeEventListener('loadedmetadata', aoCarregar);
      a.removeEventListener('timeupdate', aoTempo);
      a.removeEventListener('ended', aoFim);
    };
  }, []);

  function alternar() {
    const a = audio.current;
    if (!a) return;
    if (a.paused) {
      document.querySelectorAll('audio').forEach((x) => x !== a && x.pause());
      void a.play();
    } else a.pause();
  }

  function pular(e: MouseEvent<HTMLDivElement>) {
    const a = audio.current;
    if (!a || !duracao) return;
    const r = e.currentTarget.getBoundingClientRect();
    a.currentTime = Math.min(duracao, Math.max(0, ((e.clientX - r.left) / r.width) * duracao));
    setAtual(a.currentTime);
  }

  function trocarVelocidade() {
    const prox = VELOCIDADES[(VELOCIDADES.indexOf(vel) + 1) % VELOCIDADES.length]!;
    setVel(prox);
    if (audio.current) audio.current.playbackRate = prox;
  }

  return (
    <div className="player">
      <audio ref={audio} src={src} preload="metadata" />
      <button
        className="player-play"
        onClick={alternar}
        aria-label={tocando ? 'Pausar' : 'Tocar áudio'}
      >
        {tocando ? <Pause /> : <Play />}
      </button>
      <div className="player-meio">
        <div
          className="player-ondas"
          role="slider"
          aria-label="Posição do áudio"
          aria-valuemin={0}
          aria-valuemax={Math.round(duracao)}
          aria-valuenow={Math.round(atual)}
          tabIndex={0}
          onClick={pular}
          onKeyDown={(e) => {
            const a = audio.current;
            if (!a) return;
            if (e.key === 'ArrowRight') a.currentTime = Math.min(duracao, a.currentTime + 5);
            if (e.key === 'ArrowLeft') a.currentTime = Math.max(0, a.currentTime - 5);
          }}
        >
          {alturas.map((h, i) => (
            <span
              key={i}
              className={i / BARRAS < progresso ? 'tocada' : ''}
              style={{ height: `${h * 100}%` }}
            />
          ))}
          <span className="player-bolinha" style={{ left: `${progresso * 100}%` }} />
        </div>
        <span className="player-tempo">{mmss(tocando || atual > 0 ? atual : duracao)}</span>
      </div>
      <button className="player-vel" onClick={trocarVelocidade} aria-label="Velocidade">
        {String(vel).replace('.', ',')}×
      </button>
    </div>
  );
}
